/**
 * A casca de uma tela: a luz ambiente, o cabeçalho e o corpo.
 *
 * Cada página remontava isto à mão — `flex flex-col gap-N`, um `h1` com a classe certa (ou a
 * errada), o botão de voltar, a linha de ações à direita — e o resultado era que duas telas do
 * mesmo produto tinham respiros e tamanhos de título diferentes. Não é preciosismo: título de
 * página é uma decisão de identidade (manual §2 fixa display 700/32/-0.5px), e uma decisão que
 * mora em quinze arquivos não é uma decisão, é um hábito.
 *
 * A luz ambiente é `absolute` presa a esta casca — não `fixed` ao viewport, senão ela vaza por
 * cima da barra lateral e o menu fica tingido.
 */
import { ArrowLeft } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function Pagina({
  titulo,
  sobretitulo,
  descricao,
  icone: Icone,
  acoes,
  aoVoltar,
  children,
  className,
}: {
  titulo?: ReactNode;
  /** Rótulo mono acima do título ("EDITAR · PESSOA"). É a migalha, não um subtítulo. */
  sobretitulo?: string;
  descricao?: string;
  /** `ComponentType`, e não `LucideIcon`: é o mesmo contrato que o descriptor de recurso já
   *  declara. Apertar o tipo aqui obrigaria a afrouxá-lo lá, ou a converter em cada chamada. */
  icone?: ComponentType<{ className?: string }>;
  /** Botões à direita do título. A ação primária âmbar, se houver, é a última. */
  acoes?: ReactNode;
  aoVoltar?: () => void;
  children: ReactNode;
  className?: string;
}) {
  const temCabecalho = !!(titulo || acoes || aoVoltar);
  return (
    <div className={cn('fundo-ambiente relative flex w-full flex-col gap-6', className)}>
      {temCabecalho && (
        // `z-[1]`: acima da luz ambiente, que é um pseudo-elemento em `z-0`.
        <header className="relative z-[1] flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {aoVoltar && (
              <Button variant="fantasma" size="icon" onClick={aoVoltar} aria-label="Voltar">
                <ArrowLeft />
              </Button>
            )}
            {Icone && <Icone className="size-6 shrink-0 text-primary" />}
            <div className="min-w-0">
              {sobretitulo && <p className="tipo-rotulo">{sobretitulo}</p>}
              {titulo && <h1 className="truncate tipo-titulo">{titulo}</h1>}
              {descricao && (
                <p className="tipo-corpo-pequeno text-text-secondary">{descricao}</p>
              )}
            </div>
          </div>
          {acoes && <div className="flex shrink-0 flex-wrap items-center gap-2">{acoes}</div>}
        </header>
      )}
      <div className="relative z-[1] flex min-w-0 flex-col gap-6">{children}</div>
    </div>
  );
}
